import { Grid } from "@mui/material";
import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ApiResponse } from "src/apis";
import { useProfile } from "src/auth/Auth";
import { useSnackbar } from "src/context/ShowMessage";
import DataTable, {
  DataTableColumnProps,
} from "src/shared_components/Table/DataTable";
import { FormFieldProps } from "src/shared_components/interfaces";
import * as Yup from "yup";
import CustomLoader from "./CustomLoader";
import { useAuth } from "src/auth/AuthContext";

const SearchBar = lazy(() => import("./HeaderSearch"));

interface GridPageWrapperProps {
  label: string;
  icon?: ReactNode;
  redirectPath?: string | Function;
  columns: DataTableColumnProps[];
  heading: string;
  getData: Function;
  getDataWithClientId?: boolean;
  restoreData?: Function;
  searchLabel?: string;
  permissionCreate?: number;
  permissionUpdate?: number;
  permissionDeleteRestore?: number;
  resetData?: Object;
  disableColumnCustomization?: boolean;
  deleteData?: Function;
  module: string;
  searchFieldColumns?: FormFieldProps[];
  searchSchema?: Yup.AnyObjectSchema;
  defaultSortBy: string | null;
  sortDirection?: string;
  disableRowOnClick?: boolean;
  onViewRow?: Function;
  forSearchFieldFetchData?: Function;
  isSearchFieldNeeded?: boolean;
  id?: number;
  isHeadingNeeded?: boolean;
  isShowActionsNeeded?: boolean;
  dependency?: any;
  isApiFilter?: boolean;
  isWithIconTitle?: boolean;
  disableView?: boolean;
  fetchPDF?: Function;
  // sending email
  sendEmail?: Function;
  getEmailTemplate?: (id: number) => void;
  customDeleteMessage?: string;
}

interface TotalCounts {
  rows: number;
  pages: number;
}

const GridPageWrapper: React.FC<GridPageWrapperProps> = ({
  label = "Add New",
  redirectPath,
  heading = "",
  columns,
  getData,
  getDataWithClientId,
  restoreData,
  searchLabel,
  permissionCreate,
  permissionDeleteRestore,
  permissionUpdate,
  resetData,
  searchFieldColumns,
  searchSchema,
  disableColumnCustomization = false,
  deleteData,
  defaultSortBy,
  sortDirection,
  module = "user",
  disableRowOnClick,
  onViewRow,
  forSearchFieldFetchData,
  isSearchFieldNeeded = true,
  id,
  isHeadingNeeded = true,
  isShowActionsNeeded,
  dependency,
  isApiFilter = true,
  isWithIconTitle = false,
  disableView = false,
  fetchPDF,
  sendEmail,
  getEmailTemplate,
  customDeleteMessage,
}) => {
  const { displayMessage } = useSnackbar();
  const auth = useAuth();
  const { clientId } = useProfile();
  const location = useLocation();
  const initialStates = location.state || {};
  const [data, setData] = useState<any>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [totalCounts, setTotalCounts] = useState<TotalCounts>({
    rows: 0,
    pages: 0,
  });

  const [filters, setFilters] = useState<any>({
    sortBy: defaultSortBy,
    sortDirection: sortDirection || "asc",
    pageNumber: 1,
    pageSize: 10,
  });

  const [searchFilters, setSearchFilters] = useState<any>({
    textSearch: "",
    ...initialStates,
  });

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      let response: ApiResponse = { status: 400, data: null };
      const filterData = { ...searchFilters, ...filters };
      if (getDataWithClientId) {
        response = !id
          ? await getData(`${clientId}`, filterData)
          : await getData(id);
      } else {
        response = await getData(filterData);
      }

      if (response?.status === 200) {
        if (response?.data?.records) {
          setData(response.data.records);
          setTotalCounts({
            rows: response.data.totalRecords,
            pages: response.data.totalPages,
          });
        } else if (Array.isArray(response?.data)) {
          setData(response?.data);
          setTotalCounts({
            rows: response?.data.length,
            pages: Math.ceil(response.data.length / filters.pageSize),
          });
        }
      } else if (response?.status === 401) {
        auth.signOut();
      } else {
        displayMessage(response?.data, "error");
      }
    } catch (err) {
      displayMessage("Failed to fecth data.", "error");
    } finally {
      setDataLoading(false);
    }
  }, [
    auth,
    clientId,
    dependency,
    displayMessage,
    filters,
    getData,
    getDataWithClientId,
    id,
    searchFilters,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Grid container spacing={3} mt={-5}>
      <Grid item xs={12}>
        <Suspense fallback={<CustomLoader />}>
          <SearchBar
            title={searchLabel}
            filters={searchFilters}
            setFilters={setSearchFilters}
            setPageNumber={setFilters}
            advanceSearch={searchFieldColumns}
            schema={searchSchema}
            fetchData={forSearchFieldFetchData}
            isHeadingNeeded={isHeadingNeeded}
            heading={heading}
            module={module}
            label={label}
            redirectPath={redirectPath}
            permission={permissionCreate || 0}
            isSearchFieldNeeded={isSearchFieldNeeded}
            isWithIconTitle={isWithIconTitle}
          />
        </Suspense>
      </Grid>

      <Grid item xs={12}>
        <DataTable
          defaultColumns={columns}
          rows={data}
          filters={{ ...filters, ...searchFilters }}
          setFilters={setFilters}
          totalRowCount={totalCounts?.rows}
          moduleName={module}
          restoreData={restoreData}
          deleteData={deleteData}
          fetchData={fetchData}
          totalPageCount={totalCounts?.pages}
          loading={dataLoading}
          permissionUpdate={permissionUpdate}
          permissionDeleteRestore={permissionDeleteRestore}
          resetData={resetData}
          disableColumnCustomization={disableColumnCustomization}
          disableRowOnClick={!!disableRowOnClick}
          onViewRow={onViewRow}
          isApiFilter={isApiFilter ?? true}
          isShowActionsNeeded={isShowActionsNeeded}
          fetchPDF={fetchPDF}
          sendEmail={sendEmail}
          getEmailTemplate={getEmailTemplate}
          customDeleteMessage={customDeleteMessage}
        />
      </Grid>
    </Grid>
  );
};

export default GridPageWrapper;
